import { BsTrash } from 'react-icons/bs';

import { UserAvatar } from 'components/user-avatar';
import { DeletedTask } from 'services/dashboard.service';

import { deletedTitle, deletionReason, formatDeletedAt, formatRegisteredAt } from './deleted-tasks';

export interface DeletedTaskOwner {
  /** Matrícula do cadastrante (o próprio usuário logado). */
  _id: string;
  nomeGuerra?: string;
  avatar: { avatar_type?: number; avatar?: unknown; image?: unknown };
}

interface Props {
  task: DeletedTask;
  owner: DeletedTaskOwner;
}

const ROTULO = 'text-[11px] font-semibold uppercase tracking-wider leading-none text-grey-dark/60';

/**
 * Item resumido de uma Realização Destaque excluída: cabeçalho do cadastrante e, com rótulo, o título, a data e
 * hora da exclusão e o motivo. Só leitura, com cores atenuadas para não se confundir com o card das cadastradas.
 */
export function DeletedTaskItem({ task, owner }: Props) {
  const firstName = owner.nomeGuerra?.trim().split(/\s+/)[0] ?? '';
  const reason = deletionReason(task);

  return (
    <article className="rounded-2xl p-5 bg-maastricht-blue/60 border border-police-blue">
      <header className="flex flex-wrap items-start justify-between gap-3 pb-4 mb-4 border-b border-prussian-blue">
        <div className="flex min-w-0 items-center space-x-3">
          <div aria-hidden="true" className="pointer-events-none opacity-60">
            <UserAvatar data={owner.avatar} />
          </div>

          <div className="min-w-0">
            <p className="text-white/70 text-base font-thin leading-none flex flex-wrap items-center gap-x-1">
              <strong className="font-semibold">{firstName}</strong>
              <span>({owner._id})</span>
              <span>cadastrou</span>
            </p>
            <p className="text-police-blue text-xs font-light leading-none mt-2">{formatRegisteredAt(task.created)}</p>
          </div>
        </div>

        <span className="inline-flex items-center gap-1 rounded border border-police-blue px-2 py-1 text-[11px] font-semibold uppercase leading-none text-grey-dark/70">
          <BsTrash aria-hidden="true" />
          EXCLUÍDA
        </span>
      </header>

      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
        <div className="sm:col-span-2">
          <dt className={ROTULO}>Título</dt>
          <dd className="mt-2">
            <h3 className="text-azureish-white/70 font-semibold text-base leading-snug break-words">{deletedTitle(task)}</h3>
          </dd>
        </div>

        <div>
          <dt className={ROTULO}>Excluída em</dt>
          <dd className="mt-2 text-grey-dark/80 text-sm">{formatDeletedAt(task.deletion?.time)}</dd>
        </div>

        {reason && (
          <div>
            <dt className={ROTULO}>Motivo</dt>
            <dd className="mt-2 text-azureish-white/60 text-sm whitespace-pre-line break-words">{reason}</dd>
          </div>
        )}
      </dl>
    </article>
  );
}
